import { Unstable_Grid2 as Grid, Box } from '@mui/material';

import { ContainerColumnLeft } from '@occc-app/design-system';
import { InfoField, Chip, CardLayout } from '@occc/ui-design-system';
import { ProductInterestsDetails } from '@occc-app/routes/Audience/components/ProductInterests/ProductInterestsDetails';

import {
  AudienceCommunicationPreferenceTargetingAttributeFragment,
  AudiencePointBalanceTargetingAttributeFragment,
  AudienceTimeSinceLastVisitTargetingAttributeFragment,
  AudienceTimeSinceMembershipAcquisitionTargetingAttributeFragment,
  Month,
} from '@occc/graphql';

import { useConfig } from '@occc/config';
import { useMemo } from 'react';
import {
  getHumanReadableCommunicationOptInTargetingAttribute,
  getHumanReadableLastVisitTargetingAttribute,
  getHumanReadableMembershipCreatedTargetingAttribute,
  getHumanReadablePointBalanceTargetingAttribute,
} from './utils';
import {
  employeeStatusTitleMap,
  optInMechanismToTitleMap,
  retailerNetworkRelationOperatorMap,
} from './constants';

import { EmployeeTargetingValues } from './types';
import {
  FuelAndConvenienceProfile as FuelAndConvenienceProfileType,
  ProductInterestsAttribute,
} from '../../types';
import { HomeStoreValues } from '../HomeStore/types';
import { LocationsInfoField } from '../LocationsInfoField';
import { LocationGroupsInfoField } from '../LocationGroupsInfoField';
import { getMonthLabel } from '../Birthday/constants';

type MemberAttributesProps = {
  fuelAndConvenienceProfile: FuelAndConvenienceProfileType;
  productInterests: ProductInterestsAttribute | null;
  lastVisit?: AudienceTimeSinceLastVisitTargetingAttributeFragment;
  birthdayMonths?: Month[];
  pointBalance: AudiencePointBalanceTargetingAttributeFragment | undefined;
  membershipCreated: AudienceTimeSinceMembershipAcquisitionTargetingAttributeFragment | undefined;
  employeeStatus?: EmployeeTargetingValues | null;
  homeStore?: HomeStoreValues | null;
  activeSubBrandUuid?: string;
  communicationPreferenceCriteria?: AudienceCommunicationPreferenceTargetingAttributeFragment['criteria'];
};

export const MemberAttributes = ({
  fuelAndConvenienceProfile,
  productInterests,
  lastVisit,
  birthdayMonths,
  pointBalance,
  membershipCreated,
  employeeStatus,
  homeStore,
  activeSubBrandUuid,
  communicationPreferenceCriteria = [],
}: MemberAttributesProps) => {
  const { config } = useConfig();

  const subBrand = useMemo(() => {
    return config.subBrands?.list.find((v) => v.uuid === activeSubBrandUuid);
  }, [config.subBrands?.list, activeSubBrandUuid]);

  return (
    <ContainerColumnLeft>
      <CardLayout title='Member Attributes'>
        <Grid container gap={2}>
          {!!homeStore?.locations?.length && (
            <Grid xs={12} sm={6} md={3}>
              <LocationsInfoField label='Home Store Locations' locations={homeStore.locations} />
            </Grid>
          )}

          {!!homeStore?.locationGroups?.length && (
            <Grid xs={12} sm={6} md={3}>
              <LocationGroupsInfoField
                label='Home Store Location Groups'
                locationGroups={homeStore.locationGroups}
              />
            </Grid>
          )}

          {!!birthdayMonths?.length && (
            <Grid xs={12} sm={6} md={3}>
              <InfoField
                label='Birthday'
                value={
                  <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
                    {birthdayMonths.map((month) => (
                      <Chip key={month} label={getMonthLabel(month)} />
                    ))}
                  </Box>
                }
              />
            </Grid>
          )}

          {!!fuelAndConvenienceProfile.length &&
            fuelAndConvenienceProfile.map((item, index) => (
              <Grid key={index} xs={12} sm={6} md={3}>
                <InfoField label={item?.label} value={item?.value} />
              </Grid>
            ))}

          {productInterests && <ProductInterestsDetails productInterests={productInterests} />}

          {!!lastVisit && (
            <Grid container xs={12} sm={6} md={3}>
              <InfoField
                label='Last visited a location'
                value={getHumanReadableLastVisitTargetingAttribute(lastVisit)}
              />
            </Grid>
          )}

          {membershipCreated && (
            <Grid container xs={12} sm={6} md={3}>
              <InfoField
                label='Membership was created'
                value={getHumanReadableMembershipCreatedTargetingAttribute(membershipCreated)}
              />
            </Grid>
          )}

          {pointBalance && (
            <Grid container xs={12} sm={6} md={3}>
              <InfoField
                label='Member point balance is'
                value={getHumanReadablePointBalanceTargetingAttribute(pointBalance)}
              />
            </Grid>
          )}

          {employeeStatus?.retailerNetworkUserType && (
            <Grid xs={12} sm={6} md={3}>
              <InfoField
                label='Employee status'
                value={employeeStatusTitleMap[employeeStatus.retailerNetworkUserType]}
              />
            </Grid>
          )}

          {employeeStatus?.operator && (
            <Grid xs={12} sm={6} md={3}>
              <InfoField
                label='Relation'
                value={retailerNetworkRelationOperatorMap[employeeStatus.operator]}
              />
            </Grid>
          )}

          {!!employeeStatus?.retailerNetworks?.length && (
            <Grid xs={12} sm={6} md={3}>
              <InfoField
                label='Employed at'
                value={
                  <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
                    {employeeStatus.retailerNetworks.map((retailerNetwork) => (
                      <Chip key={retailerNetwork.uuid} label={retailerNetwork.name} />
                    ))}
                  </Box>
                }
              />
            </Grid>
          )}

          {subBrand && (
            <Grid container xs={12} sm={6} md={3}>
              <InfoField label='Member’s Active Subbrand' value={subBrand.name} />
            </Grid>
          )}

          {!!communicationPreferenceCriteria?.length &&
            communicationPreferenceCriteria.map((criterion) => {
              const criterionSubBrand = config.subBrands?.list.find(
                (item) => item.uuid === criterion.subBrandUuid,
              );

              return (
                <Grid key={criterion.mechanism} xs={12} sm={6} md={3}>
                  <InfoField
                    label={optInMechanismToTitleMap[criterion.mechanism]}
                    value={getHumanReadableCommunicationOptInTargetingAttribute(
                      criterion,
                      criterionSubBrand?.name,
                    )}
                  />
                </Grid>
              );
            })}
        </Grid>
      </CardLayout>
    </ContainerColumnLeft>
  );
};
